import App from '@/App.vue';
import router from '@/router/router';
import * as Sentry from '@sentry/vue';
import { useScriptsStore, useStylesStore } from '@/stores';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'flag-icons/css/flag-icons.min.css';
import '@/styles/style.scss';
import ToastService from 'primevue/toastservice';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import FocusTrap from 'primevue/focustrap';
import ConfirmationService from 'primevue/confirmationservice';
import setupInterceptors from '@/utils/interceptor';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import VueLazyLoad from 'vue3-lazyload';
import { createApp } from 'vue';
import i18n, { loadLocaleMessages } from '@/plugins/i18n';
import VueDeviceDetect from '@basitcodeenv/vue3-device-detect';
import VueCookies from 'vue-cookies';
import { install } from 'vue3-recaptcha-v2';
import MainPanelTheme from '@/plugins/theme';
import { useLiveChat } from '@/utils/livechat';
const { chatwoot } = useLiveChat();
const recaptchaToken = import.meta.env.VITE_RECAPTCHA_TOKEN;
const pinia = createPinia();
const app = createApp(App);
Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    environment: import.meta.env.VITE_DOMAIN,
    sendDefaultPii: true
});
// PrimeVue
app.use(PrimeVue, {
    // Default theme configuration
    theme: {
        preset: MainPanelTheme,
        options: {
            darkModeSelector: '.app-dark',
            prefix: 'p',
            cssLayer: false
        }
    },
    ripple: true
});
app.use(install, {
    sitekey: recaptchaToken,
    cnDomains: false
});
app.directive('focustrap', FocusTrap);
app.use(VueLazyLoad, {});
app.use(VueDeviceDetect);
app.use(pinia);
app.use(router);
app.use(i18n);
app.use(ConfirmationService);
app.use(ToastService);
app.use(VueCookies);
app.use(VueDOMPurifyHTML);
if (chatwoot.value) {
    app.use(chatwoot.value);
}
// Set up Axios interceptors
setupInterceptors(router);

// Load initial locale messages and styles
const locale = localStorage.getItem('locale') || 'en';
const stylesStore = useStylesStore(); // Access the styles store
const scriptsStore = useScriptsStore(); // Access the scripts store

// Function to initialize app with loaded styles
async function initializeApp() {
    try {
        // Load locale messages and styles
        await Promise.all([loadLocaleMessages(locale), stylesStore.awaitGetStyles()]);
        // Mount the app after both are loaded
        app.mount('#app');
        await scriptsStore.awaitGetScripts().catch((error) => console.error('Failed to load scripts:', error));
    } catch (error) {
        console.error('Failed to initialize the app:', error);
        app.mount('#app'); // Mount the app even if there's an error, to avoid blocking the UI
    }
}

// Call the initialize function
initializeApp();
